<template>
  <div class="review" data-xl-gen="northstar">
    <div class="review__page">
      <XlButton variant="ghost" size="sm" class="review__back" @click="goBack">
        <IconBack color="currentColor" :size="15" />
        Back to recordings
      </XlButton>

      <!--
        The page's own frame — the head and its actions, the banner, the two
        columns — with placeholders where the words and the video go. It was a
        narrow stack of four bars, so the page swapped shape when it came in
        (Recordings, AI: "loader behaviour looks a bit unusual").
      -->
      <div v-if="loading" class="review__loading" aria-busy="true" data-testid="review-loading">
        <div class="review__head">
          <div class="review__head-text review__loading-text">
            <XlSkeleton width="104px" height="24px" radius="99px" class="review__sk" />
            <XlSkeleton width="min(520px, 70%)" height="28px" radius="9px" class="review__sk review__sk--title" />
            <XlSkeleton width="min(300px, 50%)" height="0.9em" radius="6px" class="review__sk review__sk--meta" />
          </div>
          <div class="review__head-actions">
            <XlSkeleton width="112px" height="40px" radius="99px" class="review__sk" />
            <XlSkeleton width="160px" height="40px" radius="99px" class="review__sk" />
          </div>
        </div>
        <XlCard padded class="review__banner">
          <XlSkeleton width="44px" height="44px" radius="13px" class="review__sk" />
          <XlSkeleton width="min(460px, 70%)" height="0.9em" radius="6px" class="review__sk" />
        </XlCard>
        <div class="review__grid">
          <XlCard padded>
            <XlSkeleton height="auto" radius="14px" class="review__sk-video" />
            <XlSkeleton width="40%" height="0.9em" radius="6px" class="review__sk" />
          </XlCard>
          <XlCard padded class="review__rail">
            <XlSkeleton width="150px" height="0.95em" radius="6px" class="review__sk" />
            <XlSkeleton width="80%" height="0.8em" radius="6px" class="review__sk review__sk--sub" />
            <div v-for="n in 4" :key="n" class="review__sk-toggle">
              <XlSkeleton width="38px" height="22px" radius="99px" class="review__sk" />
              <XlSkeleton :width="['52%', '44%', '60%', '48%'][n - 1]" height="0.85em" radius="6px" class="review__sk" />
            </div>
          </XlCard>
        </div>
      </div>

      <p v-else-if="loadError" class="review__error" role="alert">{{ loadError }}</p>

      <template v-else>
        <!-- Before the session itself: nobody can see any of this, and the
             host is the only person who can change that. -->
        <XlCard v-if="contentHold" padded tone="warning" class="review__hold" data-testid="review-hold">
          <XlStatusIcon tone="warning">
            <IconTriangleWarning color="currentColor" />
          </XlStatusIcon>
          <div class="review__hold-text">
            <b>A speaker withdrew from this session.</b>
            <span>
              The recording, transcript and recap are not shown to members. Nothing here has been
              edited or deleted — it is simply not being shown. Cut their part, or decide it is
              fine as it is, then clear this.
            </span>
          </div>
          <XlButton :loading="clearingHold" data-testid="review-hold-clear" @click="clearContentHold">
            Clear the hold
          </XlButton>
        </XlCard>

        <header class="review__head">
          <div class="review__head-text">
            <XlTooltip :text="isApproved ? TIPS.published : TIPS.needsReview">
              <XlTag :tone="isApproved ? 'success' : 'warning'">{{ isApproved ? 'Published' : 'Needs review' }}</XlTag>
            </XlTooltip>
            <h1 class="review__title">{{ event?.title || 'Session recording' }}</h1>
            <p class="review__meta">{{ recordedLine }}</p>
          </div>
          <div class="review__head-actions">
            <XlButton v-if="!isApproved" variant="ghost" :loading="saving" :disabled="saving" @click="saveDraft">
              Save draft
            </XlButton>
            <XlButton v-if="isApproved" variant="ghost" :loading="unpublishing" :disabled="unpublishing" @click="unpublish">
              Unpublish
            </XlButton>
            <XlButton v-else :loading="approving" :disabled="approving || !canApprove" @click="requestPublish">
              Approve &amp; publish
            </XlButton>
          </div>
        </header>

        <p v-if="actionError" class="review__action-error" role="alert">
          {{ actionError }}
          <button type="button" class="review__dismiss" aria-label="Dismiss" @click="actionError = ''">×</button>
        </p>

        <XlCard padded :tone="isApproved ? 'success' : 'warning'" class="review__banner">
          <XlStatusIcon :tone="isApproved ? 'success' : 'warning'">
            <IconCheck v-if="isApproved" color="currentColor" size="23" />
            <svg v-else width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" stroke="currentColor" stroke-width="1.7" />
              <path d="M8.2 10.5V7.8a3.8 3.8 0 0 1 7.6 0v2.7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
            </svg>
          </XlStatusIcon>
          <p class="review__banner-text">
            <template v-if="isApproved">
              <b>Policyholders can see this.</b> It is in their library, and they can open it whenever they like.
            </template>
            <template v-else>
              <b>Nobody can see this yet.</b> Not policyholders, not speakers. It becomes visible the moment you publish.
            </template>
          </p>
          <XlTag v-if="!isApproved && flaggedCount" tone="error" class="review__banner-chip">
            {{ flaggedCount }} {{ flaggedCount === 1 ? 'line' : 'lines' }} to check
          </XlTag>
        </XlCard>

        <div class="review__grid">
          <div class="review__col">
            <XlCard padded>
              <video
                v-if="activePart?.video_url"
                ref="videoEl"
                class="review__video"
                :src="activePart.video_url"
                controls
                preload="metadata"
              ></video>
              <div v-else class="review__video review__video--waiting">
                <p>{{ parts.length ? 'This part is still being processed.' : 'Nothing was recorded for this session.' }}</p>
              </div>

              <template v-if="parts.length">
                <div class="review__files-head">
                  <XlTooltip v-if="parts.length > 1" underline :text="TIPS.files"><b>Recording files</b></XlTooltip>
                  <b v-else>Recording file</b>
                  <span>{{ filesMeta }}</span>
                </div>

                <template v-for="(part, i) in parts" :key="part.id">
                  <p v-if="gapBefore(i)" class="review__gap">
                    <IconTriangleWarning color="currentColor" :size="13" />
                    {{ gapBefore(i) }} — the connection dropped and the session restarted
                  </p>
                  <div
                    class="review__file"
                    :class="{ 'is-active': part.id === activePartId }"
                    role="button"
                    tabindex="0"
                    @click="onSelectPart(part)"
                    @keydown.enter.prevent="onSelectPart(part)"
                    @keydown.space.prevent="onSelectPart(part)"
                  >
                    <span class="review__file-icon" :class="{ 'is-active': part.id === activePartId }" aria-hidden="true">
                      <IconPlay v-if="part.id === activePartId" color="currentColor" size="20" />
                      <IconFilm v-else color="currentColor" :size="20" />
                    </span>
                    <span class="review__file-text">
                      <b>{{ part.display_name || partRange(part) }}</b>
                      <span>{{ partSubtitle(part, i) }}</span>
                    </span>
                    <XlButton variant="ghost" size="sm" class="review__file-name" @click.stop="openRename(part)">
                      {{ part.display_name ? 'Rename' : 'Name it' }}
                    </XlButton>
                  </div>
                </template>

                <p v-if="parts.length > 1" class="review__files-note">
                  The connection dropped mid-session, so it saved as more than one file. Policyholders watch them one
                  after the other. Give each one a name and they will know what is in it before they press play.
                </p>
              </template>
            </XlCard>

            <XlCard padded>
              <div class="review__toolbar">
                <XlTabs v-model="tab" :tabs="tabs" class="review__tabs" />
                <XlTooltip :text="TIPS.aiDraft" class="review__draft"><XlTag>AI draft</XlTag></XlTooltip>
                <span class="review__spacer"></span>
                <XlButton v-if="tab === 'transcript' && transcriptEntries.length" variant="ghost" size="sm" @click="exportTranscript">
                  Export
                </XlButton>
                <XlButton v-if="editing" variant="ghost" size="sm" :disabled="savingEdit" @click="cancelEdit">Cancel</XlButton>
                <XlButton
                  v-if="canEdit"
                  :variant="editing ? 'filled' : 'ghost'"
                  size="sm"
                  :loading="savingEdit"
                  :disabled="savingEdit"
                  @click="editing ? saveEdit() : beginEdit()"
                >
                  {{ editing ? 'Done editing' : (tab === 'transcript' ? 'Edit transcript' : 'Edit recap') }}
                </XlButton>
              </div>

              <template v-if="tab === 'transcript'">
                <XlSearchField
                  v-if="transcriptEntries.length"
                  v-model="query"
                  placeholder="Search the transcript"
                  aria-label="Search the transcript"
                  class="review__search"
                />

                <div v-if="editing && speakers.length" class="review__speakers">
                  <b>Speakers the AI picked out</b>
                  <div class="review__speaker-chips">
                    <span v-for="speaker in speakers" :key="speaker.id" class="review__speaker-chip">
                      <XlTag :tone="speaker.unnamed ? 'warning' : ''">{{ speaker.label }}</XlTag>
                      <XlButton v-if="speaker.unnamed" size="sm" class="review__speaker-name" @click="openSpeakerRename(speaker)">
                        Name them
                      </XlButton>
                    </span>
                  </div>
                  <p>Renaming a speaker changes every line they said, not just one.</p>
                </div>

                <p v-if="!transcriptEntries.length" class="review__empty">
                  No transcript has been captured for this session yet.
                </p>
                <p v-else-if="!shownEntries.length" class="review__empty">Nothing matches “{{ query }}”.</p>

                <div v-else class="review__lines">
                  <div
                    v-for="row in shownEntries"
                    :key="row.i"
                    class="review__line"
                    :class="{ 'is-sensitive': row.entry.sensitive }"
                  >
                    <span class="review__ts">{{ formatTimestamp(row.entry) }}</span>
                    <div class="review__line-body">
                      <div class="review__line-meta">
                        <b :class="{ 'is-unnamed': isUnnamed(resolveSpeaker(row.entry)) }">{{ resolveSpeaker(row.entry) }}</b>
                        <XlTooltip v-if="isLowConfidence(row.entry)" :text="TIPS.lowConfidence">
                          <XlTag tone="warning">low confidence</XlTag>
                        </XlTooltip>
                        <XlTag v-if="row.entry.sensitive" tone="error">
                          <IconTriangleWarning color="currentColor" :size="11" /> policy number said out loud
                        </XlTag>
                        <XlTag v-if="row.entry.hidden" tone="success">hidden</XlTag>
                        <XlTag v-if="row.entry.reviewed" tone="success">
                          <IconCheck color="currentColor" size="11" /> checked
                        </XlTag>
                        <select
                          v-if="editing && speakers.length > 1"
                          class="review__reassign"
                          :value="speakerIdOf(row.entry)"
                          aria-label="Who said this"
                          @change="reassign(row.entry, $event.target.value)"
                        >
                          <option v-for="speaker in speakers" :key="speaker.id" :value="speaker.id">{{ speaker.label }}</option>
                        </select>
                      </div>

                      <template v-if="editing">
                        <textarea
                          v-model="row.entry.text"
                          class="review__textarea"
                          rows="2"
                          aria-label="Edit this line"
                        ></textarea>
                        <div class="review__line-actions">
                          <XlButton variant="ghost" size="sm" @click="toggleReviewed(row.entry)">
                            {{ row.entry.reviewed ? 'Unmark' : 'Mark checked' }}
                          </XlButton>
                          <XlButton v-if="row.entry.sensitive" variant="danger" size="sm" @click="hideNumber(row.entry)">
                            Hide the number
                          </XlButton>
                          <XlButton variant="ghost-danger" size="sm" @click="removeLine(row.i)">Remove line</XlButton>
                        </div>
                      </template>
                      <p v-else class="review__line-text">
                        <template v-for="(piece, p) in highlight(row.entry.text)" :key="p">
                          <mark v-if="piece.hit">{{ piece.text }}</mark>
                          <template v-else>{{ piece.text }}</template>
                        </template>
                      </p>
                    </div>
                  </div>
                </div>

                <p v-if="transcriptEntries.length" class="review__note">
                  AI-generated transcript · may contain minor errors · {{ transcriptEntries.length }} lines ·
                  {{ reviewedCount }} checked
                </p>
              </template>

              <template v-else>
                <p v-if="!chapters.length" class="review__empty">No recap has been generated for this session yet.</p>
                <div v-else class="review__chapters">
                  <div v-for="(chapter, i) in chapters" :key="`ch-${i}`" class="review__chapter">
                    <span class="review__ts">{{ formatChapterTimestamp(chapter.start_ms) }}</span>
                    <div class="review__line-body">
                      <template v-if="editing">
                        <input v-model="chapter.title" class="review__chapter-input" aria-label="Chapter heading" />
                        <textarea v-model="chapter.summary" class="review__textarea" rows="2" aria-label="Chapter summary"></textarea>
                      </template>
                      <template v-else>
                        <b class="review__chapter-title">{{ chapter.title }}</b>
                        <p v-if="chapter.summary" class="review__line-text">{{ chapter.summary }}</p>
                      </template>
                    </div>
                  </div>
                </div>
                <p v-if="chapters.length" class="review__note">
                  AI-generated recap · a policyholder reads this instead of watching the whole session, so it is worth
                  getting right.
                </p>
              </template>
            </XlCard>
          </div>

          <div class="review__col">
            <XlCard padded class="review__rail">
              <template v-if="canManageMemberAccess">
                <b class="review__rail-title">Policyholder access</b>
                <p class="review__rail-sub">Choose what policyholders can access once this is approved and published.</p>

                <div class="review__toggle">
                  <span class="review__toggle-text">
                    <b>Recording &amp; transcript</b>
                    <span>Watch it back and read along</span>
                  </span>
                  <XlSwitch v-model="memberAccess.recordingTranscript" :disabled="!memberAccessEditing" @change="onMemberAccessChange" />
                </div>
                <div v-if="memberAccess.recordingTranscript" class="review__toggle">
                  <span class="review__toggle-text">
                    <b>Allow download</b>
                    <span>They can keep a copy of the file</span>
                  </span>
                  <XlSwitch
                    v-model="memberAccess.downloadRecordingTranscript"
                    :disabled="!memberAccessEditing"
                    @change="onMemberAccessChange"
                  />
                </div>

                <div class="review__toggle">
                  <span class="review__toggle-text">
                    <b>AI recap</b>
                    <span>The written summary with chapters</span>
                  </span>
                  <XlSwitch v-model="memberAccess.recap" :disabled="!memberAccessEditing" @change="onMemberAccessChange" />
                </div>
                <div v-if="memberAccess.recap" class="review__toggle">
                  <span class="review__toggle-text">
                    <b>Allow download</b>
                    <span>Saved as a PDF to their library</span>
                  </span>
                  <XlSwitch
                    v-model="memberAccess.downloadRecap"
                    :disabled="!memberAccessEditing"
                    @change="onMemberAccessChange"
                  />
                </div>

                <XlCard v-if="nothingShared" padded tone="error" class="review__nothing">
                  <IconTriangleWarning color="currentColor" :size="14" />
                  Both are off, so there would be nothing for a policyholder to open.
                </XlCard>

                <hr class="review__rule" />
              </template>

              <template v-if="canManageAudience">
                <XlTooltip underline :text="TIPS.audience"><b class="review__rail-title">Who can see it</b></XlTooltip>
                <div class="review__audience">
                  <XlStatusIcon :tone="audienceTone">
                    <IconUserMultiple color="currentColor" size="23" />
                  </XlStatusIcon>
                  <span class="review__audience-text">
                    <b>{{ audienceLabel }}</b>
                    <span>{{ reachLine }}</span>
                  </span>
                  <XlButton variant="ghost" size="sm" @click="managingAudience = true">Manage</XlButton>
                </div>
                <p v-if="sessionHasAudience" class="review__audience-origin">
                  Copied from the live session, {{ event?.title || 'this session' }}, when it was recorded. Changing the
                  session later does not change who can watch this.
                </p>

                <hr class="review__rule" />
              </template>

              <template v-if="canManageMemberAccess">
                <b class="review__rail-title">Sharing</b>
                <p class="review__rail-sub">Can policyholders pass this on?</p>
                <button
                  v-for="option in SHARE_OPTIONS"
                  :key="option.key"
                  type="button"
                  class="review__share"
                  :class="{ 'is-on': sharing === option.key }"
                  :aria-pressed="sharing === option.key"
                  @click="setSharing(option.key)"
                >
                  <span class="review__share-icon" :class="{ 'is-on': sharing === option.key }" aria-hidden="true">
                    <IconShare v-if="option.key === 'public'" color="currentColor" :size="19" />
                    <IconUserMultiple v-else-if="option.key === 'hub'" color="currentColor" size="19" />
                    <svg v-else width="19" height="19" viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" stroke="currentColor" stroke-width="1.7" />
                      <path d="M8.2 10.5V7.8a3.8 3.8 0 0 1 7.6 0v2.7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
                    </svg>
                  </span>
                  <span class="review__share-text">
                    <b>{{ option.title }}</b>
                    <span>{{ option.hint }}</span>
                  </span>
                </button>

                <XlButton v-if="sharing !== 'none' && shareUrl && !shareExpired" variant="ghost" class="review__copy" @click="copyShareLink">
                  <IconCopy color="currentColor" size="15" />
                  {{ copied ? 'Link copied' : 'Copy the share link' }}
                </XlButton>

                <template v-if="sharing === 'public'">
                  <p v-if="shareExpiresAt" class="review__share-expiry">
                    {{ shareExpired ? 'This link expired on' : 'This link works until' }} {{ longDate(shareExpiresAt) }}.
                    A new link stops the old one working.
                  </p>
                  <XlButton variant="ghost" class="review__copy" :loading="regenerating" @click="regenerateShareLink">
                    Get a new link
                  </XlButton>
                </template>

                <hr class="review__rule" />
              </template>

              <b class="review__rail-title">What a policyholder will see</b>
              <div class="review__preview">
                <b>{{ event?.title || 'This session' }}</b>
                <span class="review__preview-meta">{{ previewMeta }}</span>
                <template v-if="memberAccess.recordingTranscript">
                  <span v-for="part in parts" :key="`pv-${part.id}`" class="review__preview-file">
                    <IconPlay color="currentColor" size="11" />
                    {{ part.display_name || partRange(part) }}
                    <span class="review__preview-dur">{{ formatDurationMs(part.duration_ms) }}</span>
                  </span>
                </template>
                <span v-if="canShowSomething" class="review__preview-chips">
                  <template v-if="memberAccess.recordingTranscript">
                    <XlTag tone="success">Watch</XlTag>
                    <XlTag tone="success">Read the transcript</XlTag>
                  </template>
                  <XlTag v-if="memberAccess.recap" tone="success">Recap</XlTag>
                  <XlTag v-if="memberAccess.downloadRecordingTranscript || memberAccess.downloadRecap">Download</XlTag>
                  <XlTag v-if="sharing !== 'none'">Share</XlTag>
                </span>
                <span v-else class="review__preview-meta">Nothing to open.</span>
              </div>

              <XlButton v-if="isApproved" variant="ghost" class="review__rail-cta" :loading="unpublishing" @click="unpublish">
                Unpublish
              </XlButton>
              <XlButton v-else class="review__rail-cta" :loading="approving" :disabled="approving || !canApprove" @click="requestPublish">
                Approve &amp; publish
              </XlButton>
            </XlCard>
          </div>
        </div>
      </template>
    </div>

    <ManageAccessModal
      v-if="managingAudience"
      :resource="{ id: agendaEventId, name: event?.title, access: audience }"
      :members="members"
      subject-type="recording"
      :book="isHostBroker"
      @close="managingAudience = false"
      @saved="onAudienceSaved"
    />

    <XlModal
      v-if="confirmingPublish"
      title="Approve and publish?"
      subtitle="This is the moment it becomes visible. Check the three lines below."
      bordered
      @close="confirmingPublish = false"
    >
      <div class="review__confirm">
        <XlListRow v-for="line in publishSummary" :key="line.title">
          <template #leading>
            <XlStatusIcon tone="brand"><component :is="line.icon" color="currentColor" :size="18" /></XlStatusIcon>
          </template>
          <template #title>{{ line.title }}</template>
          <template #subtitle>{{ line.detail }}</template>
        </XlListRow>
        <p v-if="sharing === 'public'" class="review__confirm-warn">
          <IconTriangleWarning color="currentColor" :size="14" />
          <span><b>Open link sharing is on.</b> Anyone given the link can watch, even if they aren't a policyholder.
            Only do this for general advice.</span>
        </p>
        <p class="review__confirm-note">
          You can unpublish at any time. The edited transcript is what policyholders read — the original audio is never
          changed.
        </p>
      </div>
      <template #actions>
        <XlButton variant="ghost" @click="confirmingPublish = false">Not yet</XlButton>
        <XlButton :loading="approving" :disabled="approving" @click="approve">Approve and publish</XlButton>
      </template>
    </XlModal>

    <XlModal
      v-if="renamingPart"
      title="Name this file"
      subtitle="Policyholders see the name instead of the time it started."
      bordered
      @close="renamingPart = null"
    >
      <XlField v-model="renameValue" label="Name" name="recording-name" maxlength="120" placeholder="The talk" />
      <template #actions>
        <XlButton variant="ghost" @click="renamingPart = null">Cancel</XlButton>
        <XlButton :loading="savingName" :disabled="savingName" @click="saveName">Save</XlButton>
      </template>
    </XlModal>

    <XlModal
      v-if="renamingSpeaker"
      title="Name this speaker"
      :subtitle="`Every line ${renamingSpeaker.label} said will carry the new name.`"
      bordered
      @close="renamingSpeaker = null"
    >
      <XlField v-model="speakerNameValue" label="Name" name="speaker-name" maxlength="80" placeholder="Maya Lewis" />
      <template #actions>
        <XlButton variant="ghost" @click="renamingSpeaker = null">Cancel</XlButton>
        <XlButton :disabled="!speakerNameValue.trim()" @click="saveSpeakerName">Save</XlButton>
      </template>
    </XlModal>
  </div>
</template>

<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { selectActiveTranscript, transcriptSaveTarget } from './transcriptSelection.js';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlField from '../../xl/XlField.vue';
import XlListRow from '../../xl/XlListRow.vue';
import XlModal from '../../xl/XlModal.vue';
import XlSearchField from '../../xl/XlSearchField.vue';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import XlSwitch from '../../xl/XlSwitch.vue';
import XlTabs from '../../xl/XlTabs.vue';
import XlTag from '../../xl/XlTag.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import IconBack from '../../icons/IconBack.vue';
import IconCheck from '../../icons/IconCheck.vue';
import IconCopy from '../../icons/IconCopy.vue';
import IconFilm from '../../icons/IconFilm.vue';
import IconPaperPlane from '../../icons/IconPaperPlane.vue';
import IconPlay from '../../icons/IconPlay.vue';
import IconShare from '../../icons/IconShare.vue';
import IconTriangleWarning from '../../icons/IconTriangleWarning.vue';
import IconUserMultiple from '../../icons/IconUserMultiple.vue';
import useHubMembers from '../../../composables/useHubMembers';
import useHubBrokers from '../../../composables/useHubBrokers';
import useHubSegments from '../../../composables/useHubSegments';
import { accessSummaryLine, describeAccess, ownBookOf } from '../../../composables/useResourceAccess';
import { useAuthStore } from '../../../stores/auth';
import { ROLES } from '../../../helpers/constants';
import { compactDate, longDate, timeOfDay } from '../../../helpers/usDate';

const TIPS = {
  needsReview: 'The AI has drafted a transcript and recap. Nobody can see it until somebody reads it through and publishes it.',
  published: 'Policyholders can see this in their library right now.',
  files: 'The session saved as more than one file, usually because the connection dropped and restarted.',
  aiDraft: 'Written automatically. Worth reading before you publish it — names and numbers are where it slips.',
  audience: 'Who this is visible to. Everyone, a segment, or named people.',
  lowConfidence: "The AI wasn't sure it heard this correctly. Worth checking against the audio.",
};

// Speech-to-text confidence below which a line is worth hearing again.
const LOW_CONFIDENCE = 0.8;
const NUMBER_WORD = '(?:zero|one|two|three|four|five|six|seven|eight|nine|double|triple)';
// A run of two or more spoken numbers, or four or more digits — never a lone
// "one" in "is that the one?".
const NUMBER_RUN = new RegExp(`\\b(?:\\d+|${NUMBER_WORD})(?:[\\s.-]+(?:\\d+|${NUMBER_WORD}))+\\b|\\d{4,}`, 'gi');
const NUMBER_TOKEN = new RegExp(`\\d|\\b${NUMBER_WORD}\\b`, 'gi');

const SHARE_OPTIONS = [
  { key: 'none', title: 'No sharing', hint: 'Only the people above can ever open it' },
  { key: 'hub', title: 'Inside the hub', hint: 'A policyholder can share it with another policyholder' },
  { key: 'public', title: 'Anyone with the link', hint: 'Shareable outside the hub — good for general advice' },
];

const PLAYABLE = ['recorded', 'review_pending', 'approved', 'published'];

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const agendaEventId = computed(() => Number(route.params.agendaEventId));

const canManageMemberAccess = computed(() =>
  [ROLES.OWNER, ROLES.EVENT_HOST].includes(authStore.userRole),
);

// Only the broker who hosted the session reaches this screen as a broker, and
// the server lets that host choose the audience, inside their own book.
const isHostBroker = computed(() => authStore.userRole === ROLES.BROKER);
const canManageAudience = computed(() => canManageMemberAccess.value || isHostBroker.value);

const memberAccess = ref({
  recordingTranscript: true,
  recap: true,
  downloadRecordingTranscript: false,
  downloadRecap: false,
});
const memberAccessEditing = computed(() => !isApproved.value);

const loading = ref(true);
const saving = ref(false);
const approving = ref(false);
const unpublishing = ref(false);
const syncing = ref(false);
const loadError = ref('');
const actionError = ref('');

const event = ref(null);
const host = ref(null);
const recordings = ref([]);
const transcriptEntries = ref([]);
const speakerOverrides = ref({});
const eventTranscript = ref(null);
const recapData = ref(null);
const isApproved = ref(false);
const videoEl = ref(null);
const activePartId = ref(null);

const tab = ref('transcript');
const query = ref('');
const editing = ref(false);
const savingEdit = ref(false);
const editSnapshot = ref(null);
const renamingPart = ref(null);
const renameValue = ref('');
const savingName = ref(false);
const renamingSpeaker = ref(null);
const speakerNameValue = ref('');
const audience = ref(null);
const sessionHasAudience = ref(false);
const managingAudience = ref(false);
const confirmingPublish = ref(false);
const sharing = ref('none');
const shareUrl = ref('');
// A public link expires (XN-4870); a hub link has no expiry and this stays empty.
const shareExpiresAt = ref('');
const shareExpired = computed(() => !!shareExpiresAt.value && new Date(shareExpiresAt.value) <= new Date());
const regenerating = ref(false);

/**
 * A speaker withdrew after the session, so none of this reaches members.
 *
 * `{ reason, at }` while it stands, null once cleared. Clearing is a
 * deliberate act rather than a toggle: it says somebody looked, and it leaves
 * the host's own member-access settings exactly as they were — those were
 * never touched, which is why lifting the hold restores what they chose.
 */
const contentHold = ref(null);
const clearingHold = ref(false);

async function clearContentHold() {
  clearingHold.value = true;

  try {
    const { data } = await axios.post(`/api/agenda-events/${agendaEventId.value}/content/clear-hold`);
    contentHold.value = data?.data?.content_hold ?? null;
  } catch {
    // Left standing on purpose: a hold that silently disappears because a
    // request failed is worse than one that needs pressing twice.
  } finally {
    clearingHold.value = false;
  }
}
const copied = ref(false);

const { members, loadHubMembers } = useHubMembers();
const { segments, loadHubSegments } = useHubSegments();
const { loadHubBrokers } = useHubBrokers();
watch(() => (audience.value?.broker_ids ?? []).length, (count) => { if (count) loadHubBrokers(); });

const audienceLabel = computed(() => {
  return accessSummaryLine(audience.value ?? { all_participants: true }, members.value, segments.value, { book: isHostBroker.value, me: ownBookOf(authStore.userData) });
});
const audienceTone = computed(() => (audience.value ? describeAccess(audience.value, segments.value).tone : 'success'));
const reachLine = computed(() => {
  const reach = audience.value?.reach;
  if (typeof reach !== 'number') return 'Same as the live session';
  return `${new Intl.NumberFormat().format(reach)} ${reach === 1 ? 'person' : 'people'}`;
});

const SHARING_LINE = {
  none: 'They cannot share it',
  hub: 'They can share it with other policyholders in the hub',
  public: 'They can share a link with anyone, including people outside the hub',
};

const publishSummary = computed(() => {
  const access = memberAccess.value;
  const gets = [access.recordingTranscript && 'the recording and transcript', access.recap && 'the AI recap']
    .filter(Boolean).join(' and ');
  const downloads = (access.recordingTranscript && access.downloadRecordingTranscript) || (access.recap && access.downloadRecap);
  return [
    {
      title: 'Who gets it',
      detail: typeof audience.value?.reach === 'number' ? `${audienceLabel.value} — about ${reachLine.value}` : audienceLabel.value,
      icon: IconUserMultiple,
    },
    { title: 'What they get', detail: `${gets}${downloads ? ', and they can download it' : ', view only — no downloads'}`, icon: IconFilm },
    { title: 'Sharing', detail: SHARING_LINE[sharing.value] ?? SHARING_LINE.none, icon: IconPaperPlane },
  ];
});

const tabs = [
  { id: 'transcript', label: 'Transcript' },
  { id: 'recap', label: 'AI recap' },
];

const parts = computed(() =>
  recordings.value.filter(r => PLAYABLE.includes(r.status)).map(r => ({ ...r })),
);
const hasPendingRecordings = computed(() => recordings.value.some(r => r.status === 'recording'));
const activePart = computed(() => parts.value.find(p => p.id === activePartId.value) || parts.value[0] || null);
const chapters = computed(() => recapData.value?.chapters ?? []);
const canApprove = computed(() =>
  recordings.value.some(r => ['recorded', 'review_pending', 'approved'].includes(r.status)) || !!recapData.value,
);
const canEdit = computed(() =>
  !isApproved.value && (tab.value === 'transcript' ? transcriptEntries.value.length > 0 : chapters.value.length > 0),
);

const allowEventTranscriptFallback = computed(() => parts.value.length <= 1);
const applyActiveTranscript = () => {
  const { entries, speakerOverrides: overrides } = selectActiveTranscript(
    activePart.value,
    eventTranscript.value,
    { allowEventFallback: allowEventTranscriptFallback.value },
  );
  transcriptEntries.value = entries;
  speakerOverrides.value = overrides;
};

const speakerIdOf = (entry) => entry.participant_identity ?? entry.speaker_id ?? entry.speaker ?? '';
const resolveSpeaker = (entry) => {
  const id = speakerIdOf(entry);
  return (id && speakerOverrides.value?.[id]) || id || 'Speaker';
};
const isUnnamed = (label) => /^speaker\b/i.test(label);

const speakers = computed(() => {
  const seen = new Map();
  transcriptEntries.value.forEach((entry) => {
    const id = speakerIdOf(entry);
    if (!id || seen.has(id)) return;
    const label = resolveSpeaker(entry);
    seen.set(id, { id, label, unnamed: isUnnamed(label) });
  });
  return [...seen.values()];
});

const shownEntries = computed(() => {
  const q = query.value.trim().toLowerCase();
  return transcriptEntries.value
    .map((entry, i) => ({ entry, i }))
    .filter(row => !q || (row.entry.text || '').toLowerCase().includes(q));
});
const reviewedCount = computed(() => transcriptEntries.value.filter(e => e.reviewed).length);
const allTranscriptEntries = () => [
  ...transcriptEntries.value,
  ...parts.value.filter(p => p.id !== activePart.value?.id).flatMap(p => p.transcript?.entries ?? []),
];
const isLowConfidence = (entry) =>
  typeof entry.confidence === 'number' && entry.confidence < LOW_CONFIDENCE && !entry.reviewed;
const flaggedCount = computed(() => transcriptEntries.value.filter(e => e.sensitive || isLowConfidence(e)).length);
const hasUnhiddenNumber = computed(() => allTranscriptEntries().some(e => e.sensitive));

const highlight = (text = '') => {
  const q = query.value.trim();
  if (!q) return [{ text, hit: false }];
  const pieces = [];
  const haystack = text.toLowerCase();
  const needle = q.toLowerCase();
  let from = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, from)) {
    if (at > from) pieces.push({ text: text.slice(from, at), hit: false });
    pieces.push({ text: text.slice(at, at + needle.length), hit: true });
    from = at + needle.length;
  }
  if (from < text.length) pieces.push({ text: text.slice(from), hit: false });
  return pieces;
};

const formatTimestamp = (entry) => {
  const ms = entry.session_time_ms ?? Math.max(0, (entry.timestamp || 0) * 1000);
  return formatClock(ms);
};
const formatChapterTimestamp = (ms) => formatClock(typeof ms === 'number' && ms > 0 ? ms : 0);
const formatClock = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
const formatDurationMs = (ms) => {
  if (!ms || ms < 0) return '0 min';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};
const formatLongDate = (iso) => compactDate(iso, { long: true });

const totalDurationMs = computed(() => parts.value.reduce((sum, p) => sum + (p.duration_ms || 0), 0));
const startedAt = computed(() => parts.value.find(p => p.started_at)?.started_at ?? null);

const recordedLine = computed(() => {
  const bits = [];
  const date = startedAt.value ? formatLongDate(startedAt.value) : '';
  if (date) bits.push(`Recorded ${date}`);
  if (totalDurationMs.value > 0) bits.push(formatDurationMs(totalDurationMs.value));
  if (host.value) bits.push(`hosted by ${host.value}`);
  return bits.join(' · ');
});

const filesMeta = computed(() => {
  const date = startedAt.value ? formatLongDate(startedAt.value) : '';
  const count = `${parts.value.length} ${parts.value.length === 1 ? 'file' : 'files'}`;
  const total = totalDurationMs.value > 0 ? `, ${formatDurationMs(totalDurationMs.value)} in total` : '';
  return [date, `${count}${total}`].filter(Boolean).join(' · ');
});

const previewMeta = computed(() => {
  const bits = [];
  if (totalDurationMs.value > 0) bits.push(formatDurationMs(totalDurationMs.value));
  const day = longDate(startedAt.value);
  if (day) bits.push(day);
  return bits.join(' · ');
});

const partRange = (part) => {
  const from = part.started_at ? timeOfDay(part.started_at) : '';
  const to = part.ended_at ? timeOfDay(part.ended_at) : '';
  if (from && to) return `${from} – ${to}`;
  return from || `Part ${part.segment_index}`;
};
const partSubtitle = (part, i) => {
  const bits = [];
  if (part.display_name) bits.push(partRange(part));
  bits.push(formatDurationMs(part.duration_ms));
  bits.push(`file ${i + 1} of ${parts.value.length}`);
  return bits.join(' · ');
};
const partGapMs = (previous, current) => {
  if (!previous?.ended_at || !current?.started_at) return Number.NaN;
  return new Date(current.started_at) - new Date(previous.ended_at);
};
const gapBefore = (i) => {
  if (i === 0) return '';
  const gapMs = partGapMs(parts.value[i - 1], parts.value[i]);
  if (!Number.isFinite(gapMs) || gapMs < 60000) return '';
  const minutes = Math.round(gapMs / 60000);
  return `${minutes} minute gap`;
};

const nothingShared = computed(() => !memberAccess.value.recordingTranscript && !memberAccess.value.recap);
const canShowSomething = computed(() => !nothingShared.value);

const extractErrorMessage = (e) => {
  const data = e?.response?.data;
  return data?.error ?? data?.message ?? e?.message ?? 'Something went wrong';
};

const applyRecordings = (response) => {
  const data = response ?? {};
  recordings.value = data.recordings ?? [];
  recapData.value = data.recap || null;
  isApproved.value = !!data.recap_approved;
  host.value = data.host ?? null;
  audience.value = data.audience ?? null;
  sessionHasAudience.value = !!data.session_has_audience;
  shareUrl.value = data.share_url ?? '';
  shareExpiresAt.value = data.share_expires_at ?? '';
};

const applySettings = (settings) => {
  memberAccess.value = {
    recordingTranscript: settings.member_access_recording_transcript !== false,
    recap: settings.member_access_recap !== false,
    downloadRecordingTranscript: !!settings.download_recording,
    downloadRecap: !!settings.download_recap,
  };
  sharing.value = settings.sharing ?? 'none';
  contentHold.value = settings.content_hold ?? null;
};

const applyContent = (content) => {
  event.value = { id: agendaEventId.value, title: content.title };
  if (content.recordings) {
    const byId = new Map(content.recordings.map(r => [r.id, r]));
    recordings.value = recordings.value.map(r => ({ ...r, ...(byId.get(r.id) || {}) }));
  }
  eventTranscript.value = content.transcript || null;
};

const fillRecapLists = () => {
  if (!recapData.value) return;
  recapData.value.key_takeaways = recapData.value.key_takeaways || [];
  recapData.value.action_items = recapData.value.action_items || [];
  recapData.value.chapters = recapData.value.chapters || [];
};

const fetchAll = async () => {
  loading.value = true;
  loadError.value = '';
  try {
    const [recordingsRes, contentRes, settingsRes] = await Promise.all([
      axios.get(`/api/agenda-events/${agendaEventId.value}/recordings`),
      axios.get(`/api/agenda-events/${agendaEventId.value}/session-content`),
      axios.get(`/api/agenda-events/${agendaEventId.value}/content-settings`),
    ]);
    if (!settingsRes.data?.can_review) {
      router.replace('/forbidden');
      return false;
    }
    applyRecordings(recordingsRes.data);
    applySettings(settingsRes.data?.data ?? {});
    applyContent(contentRes.data?.data ?? {});
    fillRecapLists();

    if (parts.value.length && !activePartId.value) activePartId.value = parts.value[0].id;
    applyActiveTranscript();
  } catch (e) {
    console.error(e);
    loadError.value = extractErrorMessage(e);
  } finally {
    loading.value = false;
  }
  return true;
};

// Reconcile in the background only when something is genuinely unfinished: a
// recording still finalizing, or a session that produced nothing to read.
const loadAndSync = async () => {
  if (!(await fetchAll())) return;
  const missingContent = recordings.value.length > 0 && !transcriptEntries.value.length && !recapData.value;
  if (hasPendingRecordings.value || missingContent) backgroundSync();
};

const backgroundSync = async () => {
  if (syncing.value) return;
  syncing.value = true;
  try {
    await axios.post(`/api/agenda-events/${agendaEventId.value}/recordings/sync`);
    await fetchAll();
  } catch (e) {
    console.error('Background recording sync failed', e);
  } finally {
    syncing.value = false;
  }
};

watch(agendaEventId, (id) => { if (id) loadAndSync(); });
watch(activePartId, () => {
  if (editing.value) cancelEdit();
  applyActiveTranscript();
});
onMounted(() => {
  loadAndSync();
  if (canManageAudience.value) {
    loadHubMembers();
    loadHubSegments();
  }
});

const goBack = () => router.push({ name: 'Recordings' });

const onSelectPart = (part) => {
  activePartId.value = part.id;
  if (videoEl.value) videoEl.value.currentTime = 0;
};

const saveMemberAccess = async () => {
  const ma = memberAccess.value;
  const dlRecTr = ma.recordingTranscript && ma.downloadRecordingTranscript;
  const dlRecap = ma.recap && ma.downloadRecap;
  await axios.patch(`/api/agenda-events/${agendaEventId.value}/content-settings`, {
    member_access_recording_transcript: ma.recordingTranscript,
    member_access_recap: ma.recap,
    download_recording: dlRecTr,
    download_transcript: dlRecTr,
    download_recap: dlRecap,
  });
};

const onAudienceSaved = (saved) => {
  audience.value = saved;
  managingAudience.value = false;
};

// The link only exists while it is shared, so the server mints it on the next
// read rather than the page holding one that may already have been revoked.
const setSharing = async (mode) => {
  if (sharing.value === mode) return;
  const previous = sharing.value;
  sharing.value = mode;
  copied.value = false;
  actionError.value = '';
  try {
    const { data } = await axios.patch(`/api/agenda-events/${agendaEventId.value}/content-settings`, { sharing: mode });
    shareUrl.value = data?.share_url ?? '';
    shareExpiresAt.value = data?.share_expires_at ?? '';
  } catch (e) {
    sharing.value = previous;
    actionError.value = extractErrorMessage(e);
  }
};

// The server retires the old link, so whoever holds it needs the new one.
const regenerateShareLink = async () => {
  regenerating.value = true;
  copied.value = false;
  actionError.value = '';
  try {
    const { data } = await axios.post(`/api/agenda-events/${agendaEventId.value}/content/share-link`);
    shareUrl.value = data?.share_url ?? '';
    shareExpiresAt.value = data?.share_expires_at ?? '';
  } catch (e) {
    actionError.value = extractErrorMessage(e);
  } finally {
    regenerating.value = false;
  }
};

const copyShareLink = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
  } catch (e) {
    console.error(e);
    actionError.value = 'The link could not be copied. Select it from the address bar instead.';
  }
};

const onMemberAccessChange = async () => {
  if (!memberAccess.value.recordingTranscript) memberAccess.value.downloadRecordingTranscript = false;
  if (!memberAccess.value.recap) memberAccess.value.downloadRecap = false;
  actionError.value = '';
  try {
    await saveMemberAccess();
  } catch (e) {
    actionError.value = extractErrorMessage(e);
  }
};

// An open editor holds the only copy of what was typed. Saving or publishing
// around it would send the words it replaced and throw the new ones away.
const saveDraft = async () => {
  if (editing.value && !(await saveEdit())) return false;

  saving.value = true;
  actionError.value = '';
  try {
    if (recapData.value) {
      await axios.patch(`/api/agenda-events/${agendaEventId.value}/content/recap`, { recap: normalizedRecap() });
    }
    if (Object.keys(speakerOverrides.value).length) {
      await axios.patch(`/api/agenda-events/${agendaEventId.value}/content/transcript-speakers`, {
        speaker_overrides: speakerOverrides.value,
        recording_id: activePart.value?.id ?? null,
      });
    }
    return true;
  } catch (e) {
    actionError.value = extractErrorMessage(e);
    return false;
  } finally {
    saving.value = false;
  }
};

// The recap validator rejects a chapter whose end_ms precedes its start, so each
// chapter's end is re-derived from the next chapter's start before saving.
const normalizedRecap = () => {
  const recap = recapData.value;
  if (!recap?.chapters?.length) return recap;
  const sorted = [...recap.chapters].sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0));
  return {
    ...recap,
    chapters: sorted.map((ch, i) => ({ ...ch, end_ms: i < sorted.length - 1 ? (sorted[i + 1].start_ms ?? null) : null })),
  };
};

const requestPublish = () => {
  actionError.value = '';
  if (hasUnhiddenNumber.value) {
    actionError.value = "There's still a policy number read out loud — hide it first.";
    tab.value = 'transcript';
    return;
  }
  if (!memberAccess.value.recordingTranscript && !memberAccess.value.recap) {
    actionError.value = 'Turn on at least one thing for policyholders to see.';
    return;
  }
  confirmingPublish.value = true;
};

const approve = async () => {
  approving.value = true;
  actionError.value = '';
  try {
    if (!(await saveDraft())) return;
    await axios.post(`/api/agenda-events/${agendaEventId.value}/content/approve`);
    isApproved.value = true;
  } catch (e) {
    actionError.value = extractErrorMessage(e);
  } finally {
    approving.value = false;
    confirmingPublish.value = false;
  }
};

const unpublish = async () => {
  unpublishing.value = true;
  actionError.value = '';
  try {
    await axios.post(`/api/agenda-events/${agendaEventId.value}/content/unpublish`);
    isApproved.value = false;
  } catch (e) {
    actionError.value = extractErrorMessage(e);
  } finally {
    unpublishing.value = false;
  }
};

const beginEdit = () => {
  editSnapshot.value = JSON.parse(JSON.stringify({
    entries: transcriptEntries.value,
    recap: recapData.value,
    overrides: speakerOverrides.value,
  }));
  editing.value = true;
};

const cancelEdit = () => {
  if (editSnapshot.value) {
    transcriptEntries.value = editSnapshot.value.entries;
    speakerOverrides.value = editSnapshot.value.overrides;
    if (editSnapshot.value.recap) recapData.value = editSnapshot.value.recap;
    editSnapshot.value = null;
  }
  editing.value = false;
};

// Every transcript write rewrites the whole document, so a line's text, its
// speaker and its reviewed tick all travel together in one save.
const saveEdit = async () => {
  savingEdit.value = true;
  actionError.value = '';
  try {
    if (tab.value === 'recap') {
      await axios.patch(`/api/agenda-events/${agendaEventId.value}/content/recap`, { recap: normalizedRecap() });
    } else {
      const part = activePart.value;
      if (!part?.id) {
        actionError.value = 'No recording is selected, so there is nothing to save the transcript against.';
        return false;
      }
      const { data } = await axios.patch(
        `/api/agenda-events/${agendaEventId.value}/recordings/${part.id}/transcript`,
        { entries: transcriptEntries.value },
      );
      const target = transcriptSaveTarget(part, eventTranscript.value, {
        allowEventFallback: allowEventTranscriptFallback.value,
      });
      if (target) target.entries = data?.entries ?? transcriptEntries.value;
      if (Object.keys(speakerOverrides.value).length) {
        await axios.patch(`/api/agenda-events/${agendaEventId.value}/content/transcript-speakers`, {
          speaker_overrides: speakerOverrides.value,
          recording_id: part.id,
        });
      }
      applyActiveTranscript();
    }
    editSnapshot.value = null;
    editing.value = false;
    return true;
  } catch (e) {
    actionError.value = extractErrorMessage(e);
    return false;
  } finally {
    savingEdit.value = false;
  }
};

const toggleReviewed = (entry) => { entry.reviewed = !entry.reviewed; };
// Only the words change; the audio still has the number in it.
const hideNumber = (entry) => {
  entry.text = entry.text.replace(NUMBER_RUN, run => run.replace(NUMBER_TOKEN, '•'));
  entry.sensitive = false;
  entry.hidden = true;
};
const removeLine = (i) => { transcriptEntries.value.splice(i, 1); };
const reassign = (entry, speakerId) => {
  entry.speaker_id = speakerId;
  entry.participant_identity = speakerId;
};

const openRename = (part) => {
  renamingPart.value = part;
  renameValue.value = part.display_name || '';
};

const saveName = async () => {
  const part = renamingPart.value;
  if (!part) return;
  savingName.value = true;
  actionError.value = '';
  try {
    const { data } = await axios.patch(
      `/api/agenda-events/${agendaEventId.value}/recordings/${part.id}/name`,
      { display_name: renameValue.value.trim() },
    );
    const name = data?.recording?.display_name ?? (renameValue.value.trim() || null);
    recordings.value = recordings.value.map(r => (r.id === part.id ? { ...r, display_name: name } : r));
    renamingPart.value = null;
  } catch (e) {
    actionError.value = extractErrorMessage(e);
  } finally {
    savingName.value = false;
  }
};

const openSpeakerRename = (speaker) => {
  renamingSpeaker.value = speaker;
  speakerNameValue.value = '';
};

const saveSpeakerName = () => {
  const name = speakerNameValue.value.trim();
  if (!renamingSpeaker.value || !name) return;
  speakerOverrides.value = { ...speakerOverrides.value, [renamingSpeaker.value.id]: name };
  renamingSpeaker.value = null;
};

// The file is named for the session, so it carries the whole session: a
// connection that dropped mid-way saved as several parts, each with its own
// transcript, and exporting only the one on screen loses the rest.
const linesFor = (part) => {
  const { entries, speakerOverrides: overrides } = selectActiveTranscript(part, eventTranscript.value, {
    allowEventFallback: allowEventTranscriptFallback.value,
  });
  return entries.map((e) => {
    const id = speakerIdOf(e);
    const speaker = (id && overrides?.[id]) || id || 'Speaker';
    return `[${formatTimestamp(e)}] ${speaker}: ${e.text}`;
  });
};

const exportTranscript = () => {
  const sections = parts.value
    .map((part, i) => {
      const lines = linesFor(part);
      if (!lines.length) return '';
      const heading = parts.value.length > 1 ? `${part.display_name || `Part ${i + 1}`}\n` : '';
      return `${heading}${lines.join('\n')}`;
    })
    .filter(Boolean);

  const body = sections.length
    ? sections.join('\n\n')
    : transcriptEntries.value.map(e => `[${formatTimestamp(e)}] ${resolveSpeaker(e)}: ${e.text}`).join('\n');

  const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `transcript-event-${agendaEventId.value}.txt`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
</script>

<style scoped>
.review { min-height: 100%; }

.review__page {
  box-sizing: border-box;
  padding: var(--setup-page-padding) var(--setup-page-padding) 70px;
}

.review__back { margin-bottom: 18px; }

.review__hold {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.review__hold-text { flex: 1; min-width: 240px; }

.review__hold-text b {
  display: block;
  font-size: 15px;
  color: var(--xl-color-on-surface);
}

.review__hold-text span {
  font-size: 13px;
  line-height: 1.6;
  color: var(--xl-color-on-surface-variant);
}

.review__sk { width: auto; flex: 0 0 auto; }
.review__loading-text { display: flex; flex-direction: column; align-items: flex-start; }
/* The title's and the meta line's own line boxes. */
.review__sk--title { display: flex; align-items: center; height: 40px; margin: 8px 0 4px; }
.review__sk--meta { display: flex; align-items: center; height: 22px; font-size: 14.5px; }
.review__sk--sub { display: block; margin: 8px 0 6px; font-size: 13px; }
.review__sk-video { display: block; margin-bottom: 14px; }
.review__sk-video :deep(.xl-skeleton__bar) { aspect-ratio: 16 / 9; height: auto; }
.review__sk-toggle {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}
.review__sk-toggle:last-child { border-bottom: 0; }

.review__error,
.review__action-error {
  margin: 0 0 18px;
  padding: 13px 16px;
  border-radius: 14px;
  background: var(--xl-color-error-container, #FCEBEB);
  color: var(--xl-color-error);
  font-size: 14px;
}

.review__dismiss {
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  font-size: 17px;
  line-height: 1;
  padding: 0 2px;
}

.review__head {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.review__head-text { flex: 1; min-width: 240px; }

.review__title {
  margin: 8px 0 4px;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: var(--xl-color-primary-text);
}

.review__meta {
  margin: 0;
  font-size: 14.5px;
  color: var(--xl-color-outline);
}

.review__head-actions { display: flex; gap: 10px; flex-wrap: wrap; }

.review__banner {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 20px;
}

.review__banner-text {
  flex: 1;
  margin: 0;
  font-size: 14px;
  color: var(--xl-color-secondary-text);
}

.review__banner-text b { color: var(--xl-color-primary-text); }

.review__banner-chip { flex: 0 0 auto; }

.review__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: 20px;
}

.review__col { min-width: 0; }

.review__video {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-width: 100%;
  margin-bottom: 14px;
  border-radius: 14px;
  background: linear-gradient(150deg, #3B2547, #1C1720);
}

.review__video--waiting {
  display: grid;
  place-items: center;
  color: #fff;
}

.review__video--waiting p {
  margin: 0;
  font-size: 13.5px;
  opacity: 0.75;
  padding: 0 16px;
  text-align: center;
}

.review__files-head {
  display: flex;
  align-items: baseline;
  gap: 9px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.review__files-head b { font-size: 13.5px; color: var(--xl-color-primary-text); }
.review__files-head span { font-size: 12.5px; color: var(--xl-color-outline); }

.review__gap {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  padding: 3px 0 3px 17px;
  font-size: 12px;
  color: var(--xl-color-warning);
}

.review__file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  margin-bottom: 8px;
  border: 1px solid var(--xl-color-border-subtle);
  border-radius: 13px;
  background: #fff;
  cursor: pointer;
  transition: background 0.13s, border-color 0.13s;
}

.review__file:hover { border-color: var(--xl-color-primary-fixed-dim); background: var(--xl-color-primary-container); }
.review__file.is-active { border-color: var(--xl-color-surface-tint); background: var(--xl-color-primary-container); }
.review__file:focus-visible { outline: 2px solid var(--xl-color-surface-tint); outline-offset: 2px; }

.review__file-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 13px;
  background: var(--xl-color-border-subtle);
  color: var(--xl-color-outline);
}

.review__file-icon.is-active { background: var(--xl-color-primary-container); color: var(--xl-color-surface-tint); }

.review__file-text { flex: 1; min-width: 0; }
.review__file-text b { display: block; font-size: 14.5px; color: var(--xl-color-primary-text); }
.review__file-text span { font-size: 12.5px; color: var(--xl-color-outline); }
.review__file-name { flex: 0 0 auto; }

.review__files-note {
  margin: 11px 0 0;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.review__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/* The tab bar carries the card's own spacing here, so its stock margin and
   bottom rule would draw a second line under the toolbar. */
.review__toolbar .review__tabs { margin: 0; border: 0; gap: 20px; }

/* A tab reserves 12px under its label for the active underline, so centring the
   chip against the tab bar drops it below the words it sits beside. */
.review__draft { margin-bottom: 12px; }

.review__spacer { flex: 1; }

.review__search { margin-bottom: 14px; }

.review__speakers {
  margin-bottom: 14px;
  padding: 13px 16px;
  border: 1px solid var(--xl-color-border);
  border-radius: 14px;
  background: var(--xl-color-background);
}

.review__speakers > b { font-size: 13px; color: var(--xl-color-primary-text); }
.review__speakers p { margin: 9px 0 0; font-size: 12.5px; color: var(--xl-color-outline); }

.review__speaker-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 9px; }
.review__speaker-chip { display: inline-flex; align-items: center; gap: 5px; }

.review__speaker-chip .review__speaker-name {
  height: 24px;
  padding: 0 10px;
  font-size: 12px;
}

.review__lines { max-height: 520px; overflow-y: auto; padding-right: 4px; }

.review__line,
.review__chapter {
  display: flex;
  gap: 12px;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

.review__line { padding: 11px 0; }
.review__line.is-sensitive {
  padding-inline: 11px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--xl-color-error) 8%, white);
}

.review__confirm { display: flex; flex-direction: column; }
.review__confirm-warn {
  display: flex;
  gap: 8px;
  margin: 14px 0 0;
  padding: 13px 16px;
  border: 1px solid var(--xl-color-warning);
  border-radius: 12px;
  background: var(--xl-color-warning-container, color-mix(in srgb, var(--xl-color-warning) 16%, white));
  font-size: 13px;
  color: var(--xl-color-secondary-text);
}
.review__confirm-warn svg { flex: 0 0 auto; margin-top: 2px; color: var(--xl-color-warning); }
.review__confirm-note { margin: 14px 0 0; font-size: 12.5px; color: var(--xl-color-outline); }
.review__chapter { padding: 13px 0; }
.review__line:last-child,
.review__chapter:last-child { border-bottom: 0; }

.review__ts {
  width: 44px;
  flex: 0 0 44px;
  padding-top: 2px;
  font-size: 12.5px;
  color: var(--xl-color-outline-variant);
  font-variant-numeric: tabular-nums;
}

.review__line-body { flex: 1; min-width: 0; }

.review__line-meta {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  margin-bottom: 3px;
}

.review__line-meta b { font-size: 13px; color: var(--xl-color-primary-text); }
.review__line-meta b.is-unnamed { color: var(--xl-color-warning); }

.review__reassign {
  font-family: var(--xl-font-family);
  font-size: 12.5px;
  color: var(--xl-color-secondary-text);
  padding: 2px 6px;
  border: 1px solid var(--xl-color-border);
  border-radius: 8px;
  background: #fff;
}

.review__line-text {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
}

.review__line-text mark {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-on-primary-container);
  border-radius: 3px;
}

.review__textarea,
.review__chapter-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-family: var(--xl-font-family);
  font-size: 14px;
  line-height: 1.5;
  color: var(--xl-color-primary-text);
  padding: 9px 12px;
  border: 1px solid var(--xl-color-border);
  border-radius: 11px;
  background: #fff;
  resize: vertical;
}

.review__chapter-input { margin-bottom: 7px; font-weight: 600; resize: none; }

.review__textarea:focus-visible,
.review__chapter-input:focus-visible { outline: 2px solid var(--xl-color-surface-tint); outline-offset: -1px; }

.review__line-actions { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 7px; }

.review__chapter-title { font-size: 15px; color: var(--xl-color-primary-text); }
.review__chapter .review__line-text { margin-top: 4px; font-size: 14px; }

.review__note,
.review__empty {
  margin: 14px 0 0;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.review__empty { font-size: 14px; padding: 16px 0; }

/* Not sticky: with access, audience, sharing and the preview the rail is taller
   than most viewports, and pinning it puts its own heading behind the header. */
.review__rail { align-self: start; }

.review__rail-title { display: block; font-size: 15px; color: var(--xl-color-primary-text); }
.review__rail-sub { margin: 5px 0 6px; font-size: 13px; color: var(--xl-color-outline); }

.review__toggle {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

.review__toggle-text { flex: 1; min-width: 0; }
.review__toggle-text b { display: block; font-size: 15px; color: var(--xl-color-primary-text); }
.review__toggle-text span { font-size: 13px; color: var(--xl-color-outline); }

.review__nothing {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 10px;
  font-size: 13px;
  color: var(--xl-color-secondary-text);
}

.review__rule {
  margin: 18px 0;
  border: 0;
  border-top: 1px solid var(--xl-color-border);
}

.review__audience {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
}

.review__audience-text { flex: 1; min-width: 0; }
.review__audience-text b { display: block; font-size: 14px; color: var(--xl-color-primary-text); }
.review__audience-text span { font-size: 13px; color: var(--xl-color-outline); }

.review__audience-origin {
  margin: 0 0 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--xl-color-outline);
}

.review__share {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 7px;
  padding: 13px 15px;
  border: 1px solid var(--xl-color-border);
  border-radius: 12px;
  background: #fff;
  font-family: var(--xl-font-family);
  text-align: left;
  cursor: pointer;
}

.review__share.is-on { border-color: var(--xl-color-surface-tint); background: var(--xl-color-primary-container); }
.review__share:focus-visible { outline: 2px solid var(--xl-color-surface-tint); outline-offset: 2px; }

.review__share-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 13px;
  background: var(--xl-color-border-subtle);
  color: var(--xl-color-outline);
}

.review__share-icon.is-on { background: #fff; color: var(--xl-color-surface-tint); }

.review__share-text { flex: 1; min-width: 0; }
.review__share-text b { display: block; font-size: 14px; color: var(--xl-color-primary-text); }
.review__share-text span { font-size: 12.5px; color: var(--xl-color-outline); }

.review__copy { width: 100%; margin-top: 6px; }
.review__share-expiry { margin: 10px 0 0; font-size: 12.5px; color: var(--xl-color-outline); }

.review__preview {
  margin-top: 12px;
  padding: 15px;
  border: 1px solid var(--xl-color-border);
  border-radius: 14px;
  background: var(--xl-color-background);
}

.review__preview > b { font-size: 14.5px; color: var(--xl-color-primary-text); }

.review__preview-meta { display: block; font-size: 12.5px; color: var(--xl-color-outline); margin: 3px 0 8px; }

.review__preview-file {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  font-size: 12px;
  color: var(--xl-color-secondary-text);
}

.review__preview-dur { color: var(--xl-color-outline-variant); }

.review__preview-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }

.review__rail-cta { width: 100%; margin-top: 14px; }

@media (max-width: 900px) {
  .review__grid { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .review__page { padding: 18px 16px 60px; }
  .review__title { font-size: 24px; }
  .review__banner { flex-wrap: wrap; }
  .review__banner-text { flex-basis: calc(100% - 55px); }
  .review__banner-chip { margin-left: 55px; }
}
</style>
